import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MapPin, CreditCard, Shield, Camera, Package } from 'lucide-react'

export default function AccountPage() {
  const { user, addresses, paymentMethods } = useAuthStore()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  })

  const handleSave = () => {
    // In real app, this would call an API
    setIsEditing(false)
  }

  const defaultAddress = addresses.find(a => a.is_default)
  const defaultPayment = paymentMethods.find(p => p.is_default)

  if (!user) return null

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">Profile Information</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update your account details and personal information
            </p>
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)} variant="outline">
              Edit Profile
            </Button>
          )}
        </div>

        <div className="flex items-center gap-6 mb-6">
          <div className="relative">
            <img
              src={user.avatar}
              alt={user.name}
              className="w-24 h-24 rounded-full border-4 border-border"
            />
            {isEditing && (
              <button className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-2 shadow-lg hover:bg-primary/90 transition-colors">
                <Camera className="h-4 w-4" />
              </button>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{user.name}</h3>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            {user.is_verified && (
              <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                <Shield className="h-3 w-3" />
                Verified Account
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Full Name
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Member Since
            </label>
            <Input
              value={new Date(user.created_at).toLocaleDateString()}
              disabled
            />
          </div>
        </div>

        {isEditing && (
          <div className="flex gap-3 mt-6">
            <Button onClick={handleSave}>Save Changes</Button>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Quick Access Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/account/addresses"
          className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
        >
          <MapPin className="h-8 w-8 text-primary mb-3" />
          <h3 className="font-semibold text-foreground mb-1">Addresses</h3>
          <p className="text-sm text-muted-foreground mb-2">
            {addresses.length} saved address{addresses.length !== 1 ? 'es' : ''}
          </p>
          {defaultAddress && (
            <p className="text-xs text-muted-foreground truncate">
              Default: {defaultAddress.label}
            </p>
          )}
        </Link>

        <Link
          to="/account/payment-methods"
          className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
        >
          <CreditCard className="h-8 w-8 text-primary mb-3" />
          <h3 className="font-semibold text-foreground mb-1">Payment Methods</h3>
          <p className="text-sm text-muted-foreground mb-2">
            {paymentMethods.length} saved method{paymentMethods.length !== 1 ? 's' : ''}
          </p>
          {defaultPayment && (
            <p className="text-xs text-muted-foreground">
              Default: {defaultPayment.type}
            </p>
          )}
        </Link>

        <Link
          to="/orders"
          className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
        >
          <Package className="h-8 w-8 text-primary mb-3" />
          <h3 className="font-semibold text-foreground mb-1">Orders</h3>
          <p className="text-sm text-muted-foreground mb-2">
            View your order history
          </p>
          <p className="text-xs text-muted-foreground">
            Track and manage orders
          </p>
        </Link>
      </div>

      {/* Security Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-xl font-bold text-foreground mb-4">Security</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-foreground">Password</h3>
              <p className="text-sm text-muted-foreground">
                Last changed 3 months ago
              </p>
            </div>
            <Button variant="outline" size="sm">
              Change Password
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-foreground">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security
              </p>
            </div>
            <Button variant="outline" size="sm">
              Enable
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
